import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from './design-system';

const AboutUs = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      {/* Navigation */}
      <nav className="shadow-sm border-b" style={{ backgroundColor: 'white', borderColor: 'rgba(0, 0, 0, 0.1)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center" style={{ height: '64px' }}>
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="outline" size="small" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back</span>
                </Button>
              </Link>
              <div className="text-xl md:text-2xl font-bold" style={{ color: 'var(--color-primary)' }}>TheraEngage</div>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div style={{ backgroundColor: 'white', borderRadius: 'var(--radius-lg)', padding: 'var(--space-8)', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
          <div className="flex items-center justify-between mb-12">
            <h1 className="text-4xl md:text-5xl" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-heading)', fontWeight: 'var(--weight-bold)' }}>
              About Us
            </h1>
            <img 
              src="/icons/icon-512x512.png" 
              alt="TheraEngage Logo" 
              className="h-16 w-16 md:h-20 md:w-20"
              style={{ borderRadius: 'var(--radius-md)' }}
            />
          </div>

          <div className="space-y-12" style={{ color: 'var(--color-text)' }}>
            {/* What is TheraEngage? */}
            <section>
              <h2 className="text-3xl font-semibold mb-6" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-heading)' }}>
                What is TheraEngage?
              </h2>
              <div className="space-y-4">
                <p className="text-lg leading-relaxed" style={{ color: 'var(--color-text)', opacity: 0.9 }}>
                  TheraEngage is a digital companion for therapy.
                </p>
                <p className="text-lg leading-relaxed" style={{ color: 'var(--color-text)', opacity: 0.9 }}>
                  Therapists set goals and activities; clients complete them with daily guidance from Opie.
                </p>
                <p className="text-lg leading-relaxed" style={{ color: 'var(--color-text)', opacity: 0.9 }}>
                  This helps clients stay on track and makes therapy continuous—not just once a week.
                </p>
              </div>
            </section>

            {/* Our Mission */}
            <section>
              <h2 className="text-3xl font-semibold mb-6" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-heading)' }}>
                Our Mission
              </h2>
              <div className="space-y-4">
                <p className="text-lg leading-relaxed" style={{ color: 'var(--color-text)', opacity: 0.9 }}>
                  Our mission is to make therapy more effective by helping clients stay engaged between sessions.
                </p>
                <p className="text-lg leading-relaxed" style={{ color: 'var(--color-text)', opacity: 0.9 }}>
                  We empower therapists with tools to guide progress, strengthen connection, and improve client retention—without adding to their workload.
                </p>
              </div>
            </section>

            {/* Why We Created TheraEngage */}
            <section>
              <h2 className="text-3xl font-semibold mb-6" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-heading)' }}>
                Why We Created TheraEngage
              </h2>
              <div className="space-y-6">
                <p className="text-lg leading-relaxed" style={{ color: 'var(--color-text)', opacity: 0.9 }}>
                  Up to 40% of clients never complete their therapy homework—leading to slower progress and higher dropout rates (APA, 2022).
                </p>
                <p className="text-lg leading-relaxed" style={{ color: 'var(--color-text)', opacity: 0.9 }}>
                  The reality is simple: traditional homework isn't built for today's clients.
                </p>
                
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-heading)' }}>
                    Why?
                  </h3>
                  <p className="text-lg leading-relaxed" style={{ color: 'var(--color-text)', opacity: 0.9 }}>
                    Because once the session ends, clients often forget the assignments, lose motivation, or feel the tasks are too generic or disconnected from their real day-to-day challenges.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-heading)' }}>
                    And why does this matter?
                  </h3>
                  <p className="text-lg leading-relaxed" style={{ color: 'var(--color-text)', opacity: 0.9 }}>
                    Without consistent completion, therapists get limited visibility into how clients are doing between sessions.
                  </p>
                  <p className="text-lg leading-relaxed" style={{ color: 'var(--color-text)', opacity: 0.9 }}>
                    By the time the next session arrives, feedback is delayed and opportunities for timely adjustment are lost.
                  </p>
                </div>

                <div className="mt-8 p-6 rounded-lg" style={{ backgroundColor: 'rgba(44, 166, 164, 0.1)', borderLeft: '4px solid var(--color-primary)' }}>
                  <p className="text-lg leading-relaxed font-medium" style={{ color: 'var(--color-text)' }}>
                    That's exactly why we created TheraEngage—a modern, therapist-guided system that keeps clients engaged throughout the week, provides real-time insights, and makes therapy continuous instead of once a week.
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;

